/**
 * Polls something until it says it is done: one request at a time, quick at
 * first and then steady, honouring the server's own hint for the next wait,
 * backing off through throttling and brief outages, and giving up at a
 * deadline. A new `start`, `stop` or unmount drops whatever was pending, so a
 * slow answer can never overwrite a newer one. Used by the search and booking
 * hooks; each supplies only what to fetch and what to do with an answer.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { laneErrorStatus } from '../../../apis/lanePlanner';

/** The steady wait between polls once a search is a few seconds old. */
export const POLL_INTERVAL_MS = 3000;
/**
 * The first polls come quickly, because the vendor calls have usually been
 * running for a moment and the first quotes are often ready within a second or
 * two; later polls settle to POLL_INTERVAL_MS so a long search is not hammered.
 * Used when the server gives no hint of its own.
 */
export const POLL_RAMP_MS = [1000, 1500, 2000] as const;
const MAX_BACKOFF_MS = 30000;
/** The server's hint is followed within these bounds, so a bad value cannot hammer or stall. */
const MIN_HINT_MS = 250;
const MAX_HINT_MS = 10000;

/** Wait before poll number `pollsDone` (0 = the first one after starting). */
export const nextPollDelayMs = (pollsDone: number): number =>
  POLL_RAMP_MS[pollsDone] ?? POLL_INTERVAL_MS;

/** A poll that failed for a reason worth trying again: throttled, upstream hiccup, or no network. */
export const isTransient = (err: unknown): boolean => {
  const status = laneErrorStatus(err);
  if (status != null) return [429, 502, 503, 504].includes(status);
  const code = (err as { code?: string } | undefined)?.code;
  return code === 'ECONNABORTED' || code === 'ERR_NETWORK';
};

/** The wait a Retry-After header asks for: seconds, or an HTTP date. Null when absent or unusable. */
const retryAfterMs = (err: unknown): number | null => {
  const headers =
    (err as { headers?: Record<string, string> } | undefined)?.headers ?? {};
  const raw = headers['retry-after'] ?? headers['Retry-After'];
  if (raw == null) return null;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return seconds > 0 ? seconds * 1000 : null;
  const at = Date.parse(raw);
  return Number.isNaN(at) ? null : Math.max(at - Date.now(), 0) || null;
};

/**
 * Wait before the next poll after a failure: what the server asked for
 * (Retry-After on a 429), else a backoff that doubles with each failure in a
 * row (`failures` = 1 for the first), a throttle waiting twice as long.
 */
export const retryDelayMs = (err: unknown, failures = 1): number => {
  const asked = laneErrorStatus(err) === 429 ? retryAfterMs(err) : null;
  if (asked != null) {
    return Math.min(Math.max(asked, POLL_INTERVAL_MS), MAX_BACKOFF_MS);
  }
  const backoff = POLL_INTERVAL_MS * 2 ** Math.max(failures - 1, 0);
  const throttled = laneErrorStatus(err) === 429 ? 2 : 1;
  return Math.min(backoff * throttled, MAX_BACKOFF_MS);
};

export interface PollStep {
  /** Stop polling: there is nothing more to wait for. */
  done: boolean;
  /** The server's hint for the wait before the next poll; the ramp is used without one. */
  delayMs?: number | null;
}

export interface PollConfig<T> {
  /** One poll. The signal aborts when polling is stopped. */
  fetch: (signal: AbortSignal) => Promise<T>;
  /** Each answer: keep going or not, and how long to wait. */
  onResult: (value: T) => PollStep;
  /** A poll failed for good (not a throttle or an outage); polling has stopped. */
  onFail?: (err: unknown) => void;
  /** Keep polling through every error, not just throttles and outages. */
  retryOnAnyError?: boolean;
  /** Give up after this long since `start`. */
  timeoutMs?: number;
  /** Give up after this many polls. */
  maxPolls?: number;
  /** Called when polling gave up (the deadline or `maxPolls`). */
  onTimeout?: () => void;
  /** The wait before the first poll, instead of the ramp's first step. */
  firstDelayMs?: number;
}

export interface Poller {
  start: <T>(config: PollConfig<T>) => void;
  stop: () => void;
}

export const usePollUntil = (): Poller => {
  const generation = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadline = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  const stop = useCallback((): void => {
    generation.current += 1;
    if (timer.current) clearTimeout(timer.current);
    if (deadline.current) clearTimeout(deadline.current);
    inFlight.current?.abort();
    timer.current = null;
    deadline.current = null;
    inFlight.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(
    <T>(config: PollConfig<T>): void => {
      stop();
      const mine = generation.current;
      let polls = 0;
      let failures = 0; // in a row

      const finish = (): void => {
        if (deadline.current) clearTimeout(deadline.current);
        deadline.current = null;
      };
      const schedule = (delay: number): void => {
        timer.current = setTimeout(run, delay);
      };
      function run(): void {
        if (config.maxPolls !== undefined && polls >= config.maxPolls) {
          finish();
          config.onTimeout?.();
          return;
        }
        const controller = new AbortController();
        inFlight.current = controller;
        config
          .fetch(controller.signal)
          .then((value) => {
            if (mine !== generation.current) return;
            failures = 0;
            const step = config.onResult(value);
            if (step.done) {
              finish();
              return;
            }
            polls += 1;
            const hint = step.delayMs;
            schedule(
              hint != null && Number.isFinite(hint)
                ? Math.min(Math.max(hint, MIN_HINT_MS), MAX_HINT_MS)
                : nextPollDelayMs(polls)
            );
          })
          .catch((err) => {
            if (mine !== generation.current) return;
            if (isTransient(err) || config.retryOnAnyError) {
              polls += 1;
              failures += 1;
              schedule(retryDelayMs(err, failures));
              return;
            }
            finish();
            config.onFail?.(err);
          });
      }

      schedule(config.firstDelayMs ?? nextPollDelayMs(0));
      if (config.timeoutMs !== undefined) {
        deadline.current = setTimeout(() => {
          if (mine !== generation.current) return;
          stop();
          config.onTimeout?.();
        }, config.timeoutMs);
      }
    },
    [stop]
  );

  // stable, so an effect can list the poller as a dependency without restarting
  return useMemo(() => ({ start, stop }), [start, stop]);
};
